import { forwardRef, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './ScoopDrawer.module.scss'

/**
 * ScoopDrawer - High-performance drawer/sidebar component
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.open=false] - Whether the drawer is open
 * @param {Function} [props.onClose] - Callback when drawer should close
 * @param {'left' | 'right' | 'top' | 'bottom'} [props.anchor='left'] - Side from which the drawer slides
 * @param {'temporary' | 'persistent'} [props.variant='temporary'] - Drawer behavior variant
 *   - temporary: Closes on backdrop click, shows backdrop, renders on demand
 *   - persistent: Stays open, no backdrop, always in DOM
 * @param {string | number} [props.width='300px'] - Width for left/right drawers
 * @param {string | number} [props.height='100%'] - Height for top/bottom drawers
 * @param {boolean} [props.hasBackdrop=true] - Show backdrop overlay (only for temporary variant)
 * @param {string} [props.className] - Additional CSS classes for drawer content
 * @param {Object} [props.style] - Inline styles for drawer content
 * @param {React.ReactNode} props.children - Drawer content
 * @param {number} [props.zIndex=1500] - Z-index for the drawer
 * @param {React.Ref} ref - Forwarded ref to drawer container
 *
 * @example
 * // Temporary drawer with backdrop (default)
 * <ScoopDrawer open={isOpen} onClose={() => setIsOpen(false)} anchor="left">
 *   <div>Drawer content</div>
 * </ScoopDrawer>
 *
 * @example
 * // Persistent drawer (always in DOM, no backdrop)
 * <ScoopDrawer open={isOpen} variant="persistent" anchor="left">
 *   <div>Persistent drawer content</div>
 * </ScoopDrawer>
 */
export const ScoopDrawer = forwardRef(
    (
        {
            open = false,
            onClose,
            anchor = 'left',
            variant = 'temporary',
            width = '300px',
            height = '100%',
            hasBackdrop = true,
            className = '',
            style,
            children,
            zIndex = 1500,
            ...props
        },
        ref
    ) => {
        const [isAnimating, setIsAnimating] = useState(false)
        const [shouldRender, setShouldRender] = useState(open)
        const drawerRef = useRef(null)

        // Normalize width/height to string with units
        const normalizedWidth = typeof width === 'number' ? `${width}px` : width
        const normalizedHeight = typeof height === 'number' ? `${height}px` : height

        // Handle opening/closing animations
        useEffect(() => {
            if (open) {
                setShouldRender(true)
                // Small delay to trigger animation after render
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsAnimating(true)
                    })
                })
            } else {
                setIsAnimating(false)
                // Wait for animation to complete before unmounting
                const timer = setTimeout(() => {
                    if (variant === 'temporary') {
                        setShouldRender(false)
                    }
                }, 225) // Match animation duration
                return () => clearTimeout(timer)
            }
        }, [open, variant])

        // Handle ESC key
        useEffect(() => {
            if (!open || variant === 'persistent') return

            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    onClose?.()
                }
            }

            document.addEventListener('keydown', handleEscape)
            return () => document.removeEventListener('keydown', handleEscape)
        }, [open, onClose, variant])

        // Prevent body scroll when drawer is open (temporary variant only)
        useEffect(() => {
            if (open && variant === 'temporary') {
                const originalOverflow = document.body.style.overflow
                document.body.style.overflow = 'hidden'
                return () => {
                    document.body.style.overflow = originalOverflow
                }
            }
        }, [open, variant])

        const handleBackdropClick = (event) => {
            if (event.target === event.currentTarget) {
                onClose?.()
            }
        }

        // For persistent variant, always render in place (no portal)
        if (variant === 'persistent') {
            const drawerClassName = [
                styles.drawer,
                styles[anchor],
                styles.persistent,
                open && styles.open,
                className,
            ]
                .filter(Boolean)
                .join(' ')

            const drawerStyle = {
                '--drawer-width': normalizedWidth,
                '--drawer-height': normalizedHeight,
                ...style,
            }

            return (
                <div
                    ref={ref || drawerRef}
                    className={drawerClassName}
                    style={drawerStyle}
                    {...props}
                >
                    {children}
                </div>
            )
        }

        // For temporary variant, use portal and only render when needed
        if (!shouldRender) return null

        const backdropClassName = [styles.backdrop, isAnimating && styles.backdropVisible]
            .filter(Boolean)
            .join(' ')

        const drawerClassName = [
            styles.drawer,
            styles[anchor],
            styles.temporary,
            isAnimating && styles.open,
            className,
        ]
            .filter(Boolean)
            .join(' ')

        const drawerStyle = {
            '--drawer-width': normalizedWidth,
            '--drawer-height': normalizedHeight,
            zIndex: zIndex,
            ...style,
        }

        const drawerContent = (
            <div className={styles.drawerContainer} style={{ zIndex }}>
                {hasBackdrop && <div className={backdropClassName} onClick={handleBackdropClick} />}
                <div
                    ref={ref || drawerRef}
                    className={drawerClassName}
                    style={drawerStyle}
                    role="dialog"
                    aria-modal="true"
                    {...props}
                >
                    {children}
                </div>
            </div>
        )

        return createPortal(drawerContent, document.body)
    }
)

ScoopDrawer.displayName = 'ScoopDrawer'
